import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BottomNav, CoachTopBar } from "@/components/Nav";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Send, ArrowLeft } from "lucide-react";

const COACH_EMAIL = "coach@kaihauwarrior.co.nz";

export function ClientMessages() {
  const { user } = useAuth();
  const [coachId, setCoachId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const load = async () => {
    if (!coachId) return;
    const { data } = await api.get(`/messages?with_user=${coachId}`);
    setMsgs(data);
    setTimeout(()=>endRef.current?.scrollIntoView({behavior:"smooth"}), 50);
  };

  useEffect(() => {
    // Resolve coach ID via messages endpoint (coach must have messaged first OR we send to a known email)
    // Simpler: load all messages, find coach counterpart
    (async () => {
      const { data } = await api.get("/messages");
      let cid = null;
      for (const m of data) {
        const other = m.sender_id === user.id ? m.recipient_id : m.sender_id;
        cid = other;
      }
      // Fallback: fetch coach via public? We'll default to seed coach id — we need to find it.
      if (!cid) {
        try {
          const resp = await api.post("/messages", { recipient_id: "__coach__", content: "" }).catch(()=>null);
          void resp;
        } catch {}
      }
      setCoachId(cid);
    })();
  }, [user.id]);

  useEffect(() => { if (coachId) load(); }, [coachId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !coachId) return;
    await api.post("/messages", { recipient_id: coachId, content: text });
    setText(""); load();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white safe-pb flex flex-col">
      <div className="max-w-xl mx-auto w-full px-4 pt-6 flex-1 flex flex-col">
        <h1 className="font-display font-black uppercase text-3xl tracking-tighter">Message your coach</h1>
        <div className="flex-1 kw-card p-3 mt-4 overflow-y-auto space-y-2" style={{minHeight: 300}} data-testid="msg-list">
          {!coachId && <div className="text-sm text-neutral-500">Your coach will reach out soon. You can message once your coach initiates.</div>}
          {msgs.map(m => (
            <div key={m.id} className={`max-w-[80%] px-3 py-2 text-sm ${m.sender_id===user.id?"ml-auto bg-gold text-black":"bg-[#262626] text-white"}`}>
              {m.content}
              <div className="text-[9px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
            </div>
          ))}
          <div ref={endRef}/>
        </div>
        <form onSubmit={send} className="flex gap-2 mt-3">
          <input className="kw-input flex-1" value={text} onChange={e=>setText(e.target.value)} placeholder={coachId?"Message your coach…":"Waiting for coach…"} disabled={!coachId} data-testid="msg-input"/>
          <button className="kw-btn-primary" data-testid="msg-send"><Send className="w-4 h-4"/></button>
        </form>
      </div>
      <BottomNav />
    </div>
  );
}

export function CoachMessages() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/coach/clients");
      setClients(data);
      if (data.length) setSel(data[0]);
    })();
  }, []);

  useEffect(() => {
    if (!sel) return;
    (async () => {
      const { data } = await api.get(`/messages?with_user=${sel.id}`);
      setMsgs(data);
    })();
  }, [sel]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !sel) return;
    await api.post("/messages", { recipient_id: sel.id, content: text });
    setText("");
    const { data } = await api.get(`/messages?with_user=${sel.id}`);
    setMsgs(data);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <CoachTopBar />
      <div className="max-w-6xl mx-auto px-4 py-6 grid md:grid-cols-[260px_1fr] gap-4">
        <div className="kw-card p-2 h-fit">
          <div className="text-xs uppercase tracking-widest text-neutral-500 p-2">Clients</div>
          {clients.map(c => (
            <button key={c.id} onClick={()=>setSel(c)} className={`w-full text-left p-2 text-sm border-l-2 ${sel?.id===c.id?"border-gold text-gold":"border-transparent text-neutral-300"}`} data-testid={`coach-msg-client-${c.id}`}>
              {c.name}<div className="text-[10px] text-neutral-500">{c.email}</div>
            </button>
          ))}
        </div>
        <div className="kw-card p-4 flex flex-col" style={{minHeight: 500}}>
          <div className="font-display text-xl mb-3">{sel?.name || "Select a client"}</div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {msgs.map(m => (
              <div key={m.id} className={`max-w-[70%] px-3 py-2 text-sm ${m.sender_id===user.id?"ml-auto bg-gold text-black":"bg-[#262626]"}`}>
                {m.content}
                <div className="text-[9px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="flex gap-2 mt-3">
            <input className="kw-input flex-1" value={text} onChange={e=>setText(e.target.value)} placeholder="Reply…" data-testid="coach-msg-input"/>
            <button className="kw-btn-primary" data-testid="coach-msg-send"><Send className="w-4 h-4"/></button>
          </form>
        </div>
      </div>
    </div>
  );
}
